<template>
  <div v-if="shouldShowHover" ref="floating" :style="floatingStyles" class="floater">
    <div
      ref="floatingArrow"
      class="floatingArrow"
      :style="{
        position: 'absolute',
        width: '12px',
        height: '12px',
        background: '#fff',
        left: middlewareData.arrow?.x != null ? `${middlewareData.arrow?.x}px` : '',
        top: middlewareData.arrow?.y != null ? `${middlewareData.arrow?.y}px` : '',
        [staticSide]: '-6px',
        transform: 'rotate(45deg)',
      }"
    />
    <slot :feature="feature" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { arrow, useFloating, flip, shift, offset, type Placement } from '@floating-ui/vue'
import { useMapHover, type HoveredMapFeature } from './MapHoverState'
import { useMap } from './context'
import { debounce } from 'lodash'

const props = withDefaults(
  defineProps<{
    feature: HoveredMapFeature | null
    placement?: Placement
  }>(),
  {
    placement: 'right',
  },
)

const reference = ref<any>(null)
const floating = ref(null)
const floatingArrow = ref(null)

const { map } = useMap()
const { cursorPosition } = useMapHover()

const HOVER_DELAY = 400

const open = ref(false)

const middleware = ref([offset(18), flip(), shift(), arrow({ element: floatingArrow })])

const {
  floatingStyles,
  placement: resolvedPlacement,
  middlewareData,
} = useFloating(reference, floating, {
  middleware,
  open,
  placement: computed(() => props.placement),
})

const staticSide = computed<string>(
  () =>
    ({
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    })[resolvedPlacement.value.split('-')[0]] || '',
)

const showHoverPopup = debounce(() => {
  const feature = props.feature
  if (!feature || !map.value) return

  const anchor =
    feature.geometry.type === 'Point'
      ? (feature.geometry.coordinates as [number, number])
      : cursorPosition.value
  if (!anchor) return

  const { x: pointX, y: pointY } = map.value.project(anchor)
  const mapElement = map.value.getContainer().getBoundingClientRect()
  const x = mapElement.x + pointX
  const y = mapElement.y + pointY

  reference.value = {
    getBoundingClientRect() {
      return {
        x,
        y,
        top: y,
        left: x,
        right: x,
        bottom: y,
        width: 0,
        height: 0,
      }
    },
  }
  open.value = true
}, HOVER_DELAY)

const hideHoverPopup = () => {
  showHoverPopup.cancel()
  open.value = false
}

const shouldShowHover = computed(() => open.value && props.feature != null)

onMounted(() => map.value?.on('move', hideHoverPopup))
onBeforeUnmount(() => {
  hideHoverPopup()
  map.value?.off('move', hideHoverPopup)
})

watch(
  () => props.feature,
  (feature) => {
    hideHoverPopup()
    reference.value = null

    if (!feature || !map.value) return
    showHoverPopup()
  },
  { immediate: true },
)
</script>

<style scoped>
.floater {
  max-width: 300px;
  background: white;
  display: block;
  padding: var(--space-small);
  border-radius: var(--space-small);
}

.floatingArrow {
  width: 20px;
  height: 20px;
  transform: rotate(45deg);
}
</style>
